import { createContext, useContext, useState, useEffect } from "react";
import { authenticatedApi, publicApi } from "../lib/api";

const BannersContext = createContext();

export function BannersProvider({ children }) {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get event slug from URL
  const getEventSlug = () => {
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    return pathParts[1]; // /e/:eventSlug format
  };

  // Check if user is authenticated for current event
  const isAuthenticated = () => {
    const eventSlug = getEventSlug();
    if (!eventSlug) return false;

    const sessionKey = `attendee_session_${eventSlug}`;
    const session = localStorage.getItem(sessionKey);
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        return !!sessionData.session_token;
      } catch (error) {
        localStorage.removeItem(sessionKey);
        return false;
      }
    }
    return false;
  };

  // Load banners data
  const loadBanners = async () => {
    const eventSlug = getEventSlug();
    if (!eventSlug) return;

    try {
      setLoading(true);
      setError(null);

      const isAuth = isAuthenticated();

      // Use authenticated API if user is logged in, otherwise use public API
      const api = isAuth ? authenticatedApi : publicApi;

      const bannersData = await api.getBanners(eventSlug);

      setBanners(bannersData);
    } catch (err) {
      setError(err.message || "Failed to load banners");
    } finally {
      setLoading(false);
    }
  };

  // Load banners on mount and when authentication state changes
  useEffect(() => {
    loadBanners();
  }, []);

  // Reload banners when authentication state changes
  useEffect(() => {
    const handleStorageChange = () => {
      loadBanners();
    };

    const handleSessionUpdate = () => {
      loadBanners();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("attendee_session_updated", handleSessionUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "attendee_session_updated",
        handleSessionUpdate
      );
    };
  }, []);

  const value = {
    banners,
    loading,
    error,
    reloadBanners: loadBanners,
  };

  return (
    <BannersContext.Provider value={value}>{children}</BannersContext.Provider>
  );
}

export function useBanners() {
  const context = useContext(BannersContext);
  if (context === undefined) {
    throw new Error("useBanners must be used within a BannersProvider");
  }
  return context;
}
